import { COUNTRY_CODE, IProduct, Product, ProductOP, ProductUL } from '@cemex-core/cmx-api';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { Store } from '@ngrx/store';
import { getLegalEntity } from '@cemex/cmx-client-layout';
import { filter, map, take } from 'rxjs/operators';
import { Injector } from '@angular/core';
import { OrderBuilderState, getSelectedBusinessLine } from '@ordersnproducts/modules/order-builder/reducers';
import { ProductDescriptionHomologationService } from '@ordersnproducts/modules/shared/services/product-description-homologation.service';
import { PRODUCT_LINE_CODE } from '@ordersnproducts/modules/shared/constants/product-lines-codes';
import { getOpenPlatformProducts } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors/products.selectors';
import { ProductLineCodesTypes } from '@cemex-core/cmx-api/models/business-lines/product-lines-codes';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { Features } from 'app/shared/app-features/models/features.enum';

let appInjector: Injector;

const homologatedBusinessLines: ProductLineCodesTypes[] = [
  PRODUCT_LINE_CODE.BULK_CEMENT,
  PRODUCT_LINE_CODE.BAG_CEMENT,
  PRODUCT_LINE_CODE.READY_MIX,
];

export class Country {
  private static instance: Country;
  private store: Store<OrderBuilderState>;
  private countryCode: string;
  constructor(private injector: Injector) {
    this.store = this.injector.get<any>(Store);
  }

  public static getInstance(injector: Injector): Country {
    if (!Country.instance) {
      Country.instance = new Country(injector);
    }
    return Country.instance;
  }
  getCountryCodeProperty() {
    return this.countryCode;
  }

  getCountryCode(): void {
    if (!this.countryCode) {
      this.store
        .select(getLegalEntity)
        .pipe(
          filter((legalEntity) => !!legalEntity),
          map((legalEntity) => legalEntity.countryCode.trim()),
          take(1),
        )
        .subscribe((countryCode) => {
          this.countryCode = countryCode;
        });
    }
  }
}

/**
 * Product Description Homologation Feature
 *
 * Class that works around the use of an instance of a feature service.
 */
export class ProdDescHomoFeature {
  private serviceInstance: ProductDescriptionHomologationService;

  constructor() {
    const auxInjector: Injector = getAppInjector();
    this.serviceInstance = auxInjector.get(ProductDescriptionHomologationService);
  }

  /**
   * Calls the service function to check if the feature is enabled in rollout.
   * @returns boolean value
   */
  public isFeatureEnabled(): boolean {
    return this.serviceInstance.isEnabled();
  }
}

export class HideProductCodeFeature {
  private featureToggleService: FeatureToggleService;

  constructor() {
    const auxInjector: Injector = getAppInjector();
    this.featureToggleService = auxInjector.get(FeatureToggleService);
  }

  public isHideProductCodeEnabled(): boolean {
    return this.featureToggleService.isFeatureEnabled(Features.HIDE_PRODUCT_CODE_RMX);
  }
}

export class BusinessLine {
  private store: Store<OrderBuilderState>;
  private code: string;

  constructor() {
    const auxInjector: Injector = getAppInjector();
    this.store = auxInjector.get<any>(Store);
    this.getSelectedBusinessLineCode();
  }

  getCode(): ProductLineCodesTypes {
    return this.code as ProductLineCodesTypes;
  }

  private getSelectedBusinessLineCode(): void {
    this.store
      .select(getSelectedBusinessLine)
      .pipe(
        filter((businessLine) => !!businessLine),
        map((businessLine) => businessLine.productTypeCode),
        take(1),
      )
      .subscribe((code) => {
        this.code = code;
      });
  }
}

export class OpenPlatformProduct {
  private store: Store<OrderBuilderState>;
  private product: ProductOP;

  constructor(_product: ProductUL) {
    const auxInjector: Injector = getAppInjector();
    this.store = auxInjector.get<any>(Store);
    this.searchProduct(_product);
  }

  getProduct(): ProductOP {
    return this.product;
  }

  private searchProduct(aux: ProductUL): void {
    this.store
      .select(getOpenPlatformProducts)
      .pipe(
        filter((products) => !!products && products.length > 0),
        map((products) => {
          const foundProducts = products.filter(
            (item) =>
              item.productCode === aux.productCode &&
              item.productDesc === aux.productDesc &&
              item.productXId === aux.productXId,
          );
          if (foundProducts.length === 1) {
            return foundProducts[0];
          }
          // there's more than 1 match in ivm/products catalog
          const foundProduct = foundProducts.find((item) => {
            const i = item.technicalProducts.findIndex((technicalItem) => {
              return technicalItem.technicalProductXId === aux.technicalProduct.technicalProductXId;
            });
            return i > -1;
          });
          return foundProduct;
        }),
        take(1),
      )
      .subscribe((product) => {
        this.product = product;
      });
  }
}

/**
 * A util function that formats the product description with the homolgation rules
 * originated for UK.
 * @param {string} productDesc - The value of the property productDesc of a product object.
 * @param {string} productCode - The value of the property productCode of a product object.
 * @param {string} customerProdDesc - The value of the property customerProdDesc of a product object.
 * @returns {string} A formatted product description.
 */
function createHomologatedMixReference(
  productDesc: string,
  productCode: string,
  customerProdDesc: string,
  hideProductCode = false,
): string {
  let description = `${productDesc}`;
  if (productCode && !hideProductCode) {
    description += ` - ${productCode.replace(/^0+/, '')}`;
  }
  if (!!customerProdDesc) {
    return `${customerProdDesc} - ` + description;
  }
  return description;
}

function shouldHideProductCodeForReadyMix(productLineCode: string): boolean {
  const hideProductCodeFeature = new HideProductCodeFeature();
  return hideProductCodeFeature.isHideProductCodeEnabled() && productLineCode === PRODUCT_LINE_CODE.READY_MIX;
}

/**
 * Makes the reference to be used as an ID for products to be used only in algorithms such as
 * validations, search methods, etc.
 *
 * @param {ProductOP} product - An object of type ProductOP.
 * @returns {string} A formatted product description.
 */
export function makeReference(product: ProductOP): string {
  const injector = getAppInjector();
  const country = Country.getInstance(injector);
  country.getCountryCode();
  const countryCode = country.getCountryCodeProperty();
  const productCode = `${product.productCode.replace(/^0+/, '')}`;
  if ([COUNTRY_CODE.UNITED_KINGDOM, COUNTRY_CODE.FRANCE].includes(countryCode as COUNTRY_CODE)) {
    if (!!product['customerProdDesc']) {
      return `${_safe(() => product['customerProdDesc'])} - ${productCode}`;
    }
    return `${productCode} - ${_safe(() => product.productDesc, '')}`;
  }
  return productCode;
}

/**
 * New mix reference structure for Multiple Orders Online (MOO) module for UK.
 *
 * Creates a product description to be used only in algorithms such as validations, search methods, etc.
 *
 * @param {ProductOP} product - An object of type ProductOP.
 * @returns {string} A formatted product description.
 */
export function makeReferenceMO(product: ProductOP): string {
  if (!!product['customerProdDesc']) {
    return `${product['customerProdDesc']} - ${product.productCode.replace(/^0+/, '')} - ${_safe(() => product.productDesc, '')}`;
  } else {
    return `${product.productCode.replace(/^0+/, '')} - ${_safe(() => product.productDesc, '')}`;
  }
}

/**
 * Creates a homologated mix reference description for Multiple Orders Online (MOO) module for UK.
 *
 * This function only checks if the feature is enabled, MOO only works for RMX business line.
 * @param {ProductOP} product - An object of type ProductOP.
 * @returns {string} A formatted product description.
 */
export function makeReferenceDescriptionMOO(product: ProductOP): string {
  const feature = new ProdDescHomoFeature();
  const hideProductCode = shouldHideProductCodeForReadyMix(PRODUCT_LINE_CODE.READY_MIX);

  if (feature.isFeatureEnabled()) {
    return createHomologatedMixReference(
      product.productDesc,
      product.productCode,
      product['customerProdDesc'],
      hideProductCode,
    );
  } else {
    const description = _safe(() => product.productDesc, '');
    const productCode = product.productCode ? product.productCode.replace(/^0+/, '') : '';
    if (!!product['customerProdDesc']) {
      if (hideProductCode || !product.productCode) {
        return `${product['customerProdDesc']} - ${description}`;
      } else {
      return `${product['customerProdDesc']} - ${productCode} - ${description}`;

      }
    } else {
      if (hideProductCode || !product.productCode) {
        return description;
      } else {
      return `${productCode} - ${description}`;
      }

    }
  }
}

/**
 * Check if the feature for mix reference homolgation is on. If so, return new format for the
 * product description.
 * @param {Product} product - An object of type Product.
 * @param {string} productTypeCode - The value of the business line that the product belongs to.
 * @returns {string} A formatted product description.
 */
export function makeHomologatedMixReference(product: Product, productTypeCode: string): string {
  const feature = new ProdDescHomoFeature();
  const hideProductCode = shouldHideProductCodeForReadyMix(productTypeCode);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(productTypeCode as ProductLineCodesTypes)) {
    if (product.commercialDesc && product.commercialCode) {
      return createHomologatedMixReference(
        product.commercialDesc,
        product.commercialCode.replace(/^0+/, ''),
        product['customerProdDesc'],
        hideProductCode,
      );
    }
    return createHomologatedMixReference(
      product.commercialDesc,
      product.productCode.replace(/^0+/, ''),
      product['customerProdDesc'],
      hideProductCode,
    );
  } else {
    let code: string;
    if (product.productCode) {
      code = product.productCode.replace(/^0+/, '');
    } else if (product.commercialCode) {
      code = product.commercialCode.replace(/^0+/, '');
    }
    const description = `${hideProductCode ? '' : `${code} - `}${_safe(() => product.commercialDesc, '')}`;
    if (!!product['customerProdDesc']) {
      return `${product['customerProdDesc']} - ` + description;
    }
    return description;
  }
}

/**
 * Check if the feature for mix reference homologation is on. If so, return new format for the
 * product description. To avoid legacy issues, this function was created to target CEMENT business logic.
 * @param {Product} product - An object of type Product.
 * @param {string} productTypeCode - The value of the business line that the product belongs to.
 * @returns {string} A formatted product description.
 */
export function makeReferenceProducDescHomologationCement(
  product: Product,
  productTypeCode: string,
  legacyFormat?: string,
): string {
  const feature = new ProdDescHomoFeature();
  const CEMENT: ProductLineCodesTypes[] = [PRODUCT_LINE_CODE.BULK_CEMENT, PRODUCT_LINE_CODE.BAG_CEMENT];
  if (feature.isFeatureEnabled() && CEMENT.includes(productTypeCode as ProductLineCodesTypes)) {
    if (product.commercialDesc) {
      return createHomologatedMixReference(product.commercialDesc, product.productCode, product['customerProdDesc']);
    }
    return createHomologatedMixReference(product.productDesc, product.productCode, product['customerProdDesc']);
  } else {
    if (legacyFormat) {
      return legacyFormat;
    } else {
      return `${product.productCode.replace(/^0+/, '')} - ${product.productDesc}`;
    }
  }
}

/**
 * Check if the feature for mix reference homologation is on. If so, return new format for the
 * product description. To avoid legacy issues, this function was created to target CEMENT business logic.
 * @param {Product} product - An object of type Product.
 * @param {string} productTypeCode - The value of the business line that the product belongs to.
 * @returns {string} A formatted product description.
 */
export function makeHomologatedMixReferenceCEMENT(product: Product, productTypeCode: string): string {
  const feature = new ProdDescHomoFeature();
  const CEMENT: ProductLineCodesTypes[] = [PRODUCT_LINE_CODE.BULK_CEMENT, PRODUCT_LINE_CODE.BAG_CEMENT];
  if (feature.isFeatureEnabled() && CEMENT.includes(productTypeCode as ProductLineCodesTypes)) {
    if (product.commercialDesc) {
      return createHomologatedMixReference(product.commercialDesc, product.productCode, product['customerProdDesc']);
    }
    return createHomologatedMixReference(product.productDesc, product.productCode, product['customerProdDesc']);
  } else {
    return `${product.productCode.replace(/^0+/, '')} - ${product.productDesc}`;
  }
}

export function setAppInjector(injector: Injector) {
  if (!appInjector) {
    appInjector = injector;
  }
}

export function getAppInjector(): Injector {
  return appInjector;
}

export function makeReferenceDetail(product: ProductOP): string {
  if (!!product['productMixReference']) {
    const mixReferenceCode = `${_safe(() => product['productMixReference'])} - ${product.productCode.replace(
      /^0+/,
      '',
    )}`;
    return mixReferenceCode;
  } else {
    return `${product.productCode.replace(/^0+/, '')} - ${product['commercialDesc'].replace(/^0+/, '')}`;
  }
}

/**
 * Check if the feature for mix reference homologation is on. If so, return new format for the
 * product description.
 * @param {ProductOP} product - An object of type ProductOP.
 * @param {string} productLineCode - The value of the business line that the product belongs to.
 * @returns {string} A formatted product description.
 */
export function makeReferenceDescription(product: ProductOP, productLineCode: string): string {
  const feature = new ProdDescHomoFeature();
  const hideProductCode = shouldHideProductCodeForReadyMix(productLineCode);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(productLineCode as ProductLineCodesTypes)) {
    return createHomologatedMixReference(product.productDesc, product.productCode, product['customerProdDesc'], hideProductCode);
  } else {
    let description = `${product.productCode.replace(/^0+/, '')} - ${_safe(() => product.productDesc, '')}`;
    if (hideProductCode) {
      description = `${_safe(() => product.productDesc, '')}`;
    }
    if (!!product['customerProdDesc']) {
      return `${product['customerProdDesc']} - ` + description;
    }
    return description;
  }
}

/**
 *
 * @param product Reference used for FR; uses 'mixReference' only as a nickname for the product
 * customerProdDesc -> equivalent to mixReference
 * @returns
 */
export function makeProductDescriptionReference(product: ProductOP): string {
  if (!!product['customerProdDesc']) {
    return `${product['customerProdDesc']} - ${product.productCode.replace(/^0+/, '')} / ${_safe(
      () => product['SalesText'],
      product.productDesc,
    )}`;
  } else {
    return `${product.productCode.replace(/^0+/, '')} - ${_safe(() => product['SalesText'], product.productDesc)}`;
  }
}

/**
 * Function to format the product description in the order confirmation view.
 * A specific one was created in order to comply with the legacy format for the description.
 * @param {ProductOP} product - A ProductOP type object.
 * @param {string} businessLineCode - The code for the selected business line.
 * @returns {string} A formatted product description.
 */
export function formatOrderConfirmedMixReference(product: ProductOP, businessLineCode: string): string {
  const feature = new ProdDescHomoFeature();
  const injector = getAppInjector();
  const country = Country.getInstance(injector);
  country.getCountryCode();
  const countryCode = country.getCountryCodeProperty();
  const hideProductCode = shouldHideProductCodeForReadyMix(businessLineCode as ProductLineCodesTypes);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(businessLineCode as ProductLineCodesTypes)) {
    return createHomologatedMixReference(product.productDesc, product.productCode, product['customerProdDesc'], hideProductCode);
  } else {
    if (countryCode === COUNTRY_CODE.USA) {
      return product.productCode.replace(/^0+/, '') + ' / ' + product.productDesc;
    }
    return product.productDesc;
  }
}

/**
 * Function to format the product description for the TopProducts method for the
 * configurable form component.
 *
 * A specific one was created in order to comply with the legacy product interface.
 * @param {ProductOP} product - A IProduct type object.
 * @param {string} businessLineCode - The code for the selected business line.
 * @returns {string} A formatted product description.
 */
export function formatTopProductsMixReference(product: IProduct, businessLineCode: string): string {
  const feature = new ProdDescHomoFeature();
  const hideProductCode = shouldHideProductCodeForReadyMix(businessLineCode as ProductLineCodesTypes);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(businessLineCode as ProductLineCodesTypes)) {
    if (product['commercialMixRef']) {
      return createHomologatedMixReference(product.commercialDesc, product.commercialCode, product['commercialMixRef'], hideProductCode);
    }
    return createHomologatedMixReference(product.commercialDesc, product.commercialCode, product['customerProdDesc'], hideProductCode);
  } else {
    if (hideProductCode) {
      if (!!product['productCode']) {
        // eslint-disable-next-line semi
        return `${product['productDesc']}`
      } else {
        return `${product.commercialDesc}`;
      }
    }
    if (!!product['productCode']) {
      return `${product['productCode'].replace(/^0+/, '')} / ${product['productDesc']}`;
    } else {
      return `${product.commercialCode.replace(/^0+/, '')} / ${product.commercialDesc}`;
    }
  }
}

/**
 * Function to format the product description for the TopProducts method for the
 * configurable form component.
 * @param {ProductOP} product - A ProductOP type object.
 * @param {string} businessLineCode - The code for the selected business line.
 * @returns {string} A formatted product description.
 */
export function formatOpenPlatformTopProductsMixReference(product: ProductOP, businessLineCode: string): string {
  const feature = new ProdDescHomoFeature();
  const hideProductCode = shouldHideProductCodeForReadyMix(businessLineCode as ProductLineCodesTypes);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(businessLineCode as ProductLineCodesTypes)) {
    return createHomologatedMixReference(product.productDesc, product.productCode, product['customerProdDesc'], hideProductCode);
  } else {
    if (hideProductCode) {
      return product.productDesc;
    }
    return `${product.productCode.replace(/^0+/, '')} / ${product.productDesc}`;
  }
}

/**
 * Function to format the product description for the product card component.
 *
 * It has a very specific condition to get the code and description, so they were implemented as parameters
 * to comply with the legacy code.
 * @param product {Product} - An object of type Product.
 * @param defaultCode {string} - The result of a method for getting the product code in product card component class.
 * @param defaultDesc {string} - The result of a method for getting the product description in product card component class.
 * @returns A formatted product description.
 */
export function formatProductCardHeaderMixReference(
  product: Product,
  defaultCode: string,
  defaultDesc: string,
): string {
  const feature = new ProdDescHomoFeature();
  const businessLine = new BusinessLine();
  const hideProductCode = shouldHideProductCodeForReadyMix(businessLine.getCode() as ProductLineCodesTypes);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(businessLine.getCode())) {
    if (product['commercialMixRef']) {
      return createHomologatedMixReference(product.commercialDesc, product.commercialCode, product['commercialMixRef'], hideProductCode);
    }
    if (product.commercialDesc) {
      let code = product.commercialCode;
      if (product.productCode) {
        code = product.productCode;
      }
      return createHomologatedMixReference(product.commercialDesc, code, product['customerProdDesc'], hideProductCode);
    }
    return createHomologatedMixReference(product.productDesc, product.commercialCode, product['customerProdDesc'], hideProductCode);
  } else {
    return defaultCode + '-' + defaultDesc;
  }
}

/**
 * Creates a product description to be used with the download callback of a created order's excel file.
 * @param {ProductUL} product - An object of type ProductUL.
 * @returns {string} A formatted product description.
 */
export function formatDescriptionForExcelFile(product: ProductUL): string {
  const feature = new ProdDescHomoFeature();
  const businessLine = new BusinessLine();
  const hideProductCode = shouldHideProductCodeForReadyMix(businessLine.getCode() as ProductLineCodesTypes);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(businessLine.getCode())) {
    const openPlatformProduct = new OpenPlatformProduct(product);
    const aux = openPlatformProduct.getProduct();
    return createHomologatedMixReference(aux.productDesc, aux.productCode, aux['customerProdDesc'], hideProductCode);
  }
  if (hideProductCode) {
    return product.productDesc;
  }
  return product.productCode + ' - ' + product.productDesc;
}

/**
 * Creates a product description to be used with the notification/CONFIRMED_ORDER/EMAIL callback.
 * @param {ProductOP} product - An object of type ProductOP.
 * @returns {string} A formatted product description.
 */
export function formatDescriptionForNotifcationEmail(product: ProductOP): string {
  const feature = new ProdDescHomoFeature();
  const businessLine = new BusinessLine();
  const hideProductCode = shouldHideProductCodeForReadyMix(businessLine.getCode() as ProductLineCodesTypes);

  if (feature.isFeatureEnabled() && homologatedBusinessLines.includes(businessLine.getCode())) {
    return createHomologatedMixReference(product.productDesc, product.productCode, product['customerProdDesc'], hideProductCode);
  }
  return product.productDesc;
}
